import { useState, useEffect, useCallback } from 'react';
import {
  Search, Filter, X, Loader2, Truck, AlertTriangle, CheckCircle2, XCircle,
  Clock, ChevronDown, ChevronUp, Eye, RotateCcw, RefreshCw, Edit3, TimerReset,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR, formatDate } from '@/utils/formatters';

// ==================== Types ====================
interface EWayBillLog {
  id: string;
  ewayBillNo: string | null;
  invoiceId: string | null;
  invoiceNumber: string | null;
  shipmentId: string | null;
  shipmentRef: string | null;
  fromPlace: string;
  toPlace: string;
  totalValue: number | string;
  validUntil: string | null;
  status: 'GENERATED' | 'ACTIVE' | 'EXPIRED' | 'CANCELLED';
  vehicleNumber: string | null;
  vehicleType: string | null;
  transporterId: string | null;
  transporterName: string | null;
  errorMessage: string | null;
  generatedAt: string | null;
  cancelledAt: string | null;
  createdAt: string;
}

interface EWayBillStats {
  total: number;
  active: number;
  expired: number;
  cancelled: number;
  generated: number;
}

const STATUS_COLORS: Record<string, string> = {
  GENERATED: 'bg-green-100 text-green-700',
  ACTIVE: 'bg-blue-100 text-blue-700',
  EXPIRED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-gray-100 text-gray-500',
};

const STATUS_OPTIONS = ['GENERATED', 'ACTIVE', 'EXPIRED', 'CANCELLED'];

// ==================== Utility ====================
function getExpiryColor(validUntil: string | null): string {
  if (!validUntil) return '';
  const now = new Date();
  const expiry = new Date(validUntil);
  const diffMs = expiry.getTime() - now.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);
  if (diffHours < 0) return 'text-red-600 font-medium';
  if (diffHours < 24) return 'text-yellow-600 font-medium';
  return 'text-green-600';
}

function formatValidUntil(validUntil: string | null): string {
  if (!validUntil) return '-';
  const d = new Date(validUntil);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

// ==================== Main Component ====================
export function EWayBillDashboard() {
  const [logs, setLogs] = useState<EWayBillLog[]>([]);
  const [stats, setStats] = useState<EWayBillStats>({ total: 0, active: 0, expired: 0, cancelled: 0, generated: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const [sortField, setSortField] = useState('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Action states
  const [generating, setGenerating] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);

  // Modal states
  const [detailLog, setDetailLog] = useState<EWayBillLog | null>(null);
  const [cancelTarget, setCancelTarget] = useState<EWayBillLog | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [vehicleTarget, setVehicleTarget] = useState<EWayBillLog | null>(null);
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [vehicleType, setVehicleType] = useState('Regular');
  const [vehicleSaving, setVehicleSaving] = useState(false);
  const [extendTarget, setExtendTarget] = useState<EWayBillLog | null>(null);
  const [extendReason, setExtendReason] = useState('');
  const [extendSaving, setExtendSaving] = useState(false);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = { page: String(page), limit: String(limit) };
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (sortField) params.sortBy = sortField;
      if (sortDir) params.sortDir = sortDir;
      const res = await api.get('/gst-compliance/ewaybill/logs', { params });
      setLogs(res.data.data?.logs ?? res.data.data ?? []);
      setTotal(res.data.pagination?.total ?? res.data.data?.total ?? 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load e-Way Bill logs');
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, dateFrom, dateTo, sortField, sortDir, page]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/gst-compliance/ewaybill/stats');
      setStats(res.data.data ?? { total: 0, active: 0, expired: 0, cancelled: 0, generated: 0 });
    } catch {
      // Non-critical
    }
  }, []);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  useEffect(() => { setPage(1); }, [search, filterStatus, dateFrom, dateTo]);

  const totalPages = Math.ceil(total / limit);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  // Generate EWB
  const handleGenerate = async (log: EWayBillLog) => {
    setGenerating(log.id);
    try {
      await api.post('/gst-compliance/ewaybill/generate', {
        invoiceId: log.invoiceId,
        shipmentId: log.shipmentId,
      });
      fetchLogs();
      fetchStats();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to generate e-Way Bill');
    } finally {
      setGenerating(null);
    }
  };

  // Cancel EWB
  const handleCancel = async () => {
    if (!cancelTarget || !cancelReason.trim()) return;
    setCancelling(cancelTarget.id);
    try {
      await api.post('/gst-compliance/ewaybill/cancel', {
        ewayBillNo: cancelTarget.ewayBillNo,
        reason: cancelReason,
      });
      setCancelTarget(null);
      setCancelReason('');
      fetchLogs();
      fetchStats();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to cancel e-Way Bill');
    } finally {
      setCancelling(null);
    }
  };

  // Update Vehicle
  const handleUpdateVehicle = async () => {
    if (!vehicleTarget || !vehicleNumber.trim()) return;
    setVehicleSaving(true);
    try {
      await api.post('/gst-compliance/ewaybill/update-vehicle', {
        ewayBillNo: vehicleTarget.ewayBillNo,
        vehicleNumber: vehicleNumber.toUpperCase(),
        vehicleType,
      });
      setVehicleTarget(null);
      setVehicleNumber('');
      fetchLogs();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update vehicle');
    } finally {
      setVehicleSaving(false);
    }
  };

  // Extend Validity
  const handleExtendValidity = async () => {
    if (!extendTarget || !extendReason.trim()) return;
    setExtendSaving(true);
    try {
      await api.post('/gst-compliance/ewaybill/extend-validity', {
        ewayBillNo: extendTarget.ewayBillNo,
        reason: extendReason,
      });
      setExtendTarget(null);
      setExtendReason('');
      fetchLogs();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to extend validity');
    } finally {
      setExtendSaving(false);
    }
  };

  const canCancelEWB = (log: EWayBillLog) => {
    if (!['GENERATED', 'ACTIVE'].includes(log.status) || !log.generatedAt) return false;
    const generated = new Date(log.generatedAt);
    const now = new Date();
    return (now.getTime() - generated.getTime()) < 24 * 60 * 60 * 1000;
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">e-Way Bill Dashboard</h1>
          <p className="text-sm text-gray-500">Generate and manage e-Way Bills for goods movement</p>
        </div>
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Total Generated" value={stats.generated} icon={<Truck size={18} />} color="text-gray-900" />
        <StatCard label="Active" value={stats.active} icon={<CheckCircle2 size={18} />} color="text-blue-600" />
        <StatCard label="Expired" value={stats.expired} icon={<AlertTriangle size={18} />} color="text-red-600" />
        <StatCard label="Cancelled" value={stats.cancelled} icon={<XCircle size={18} />} color="text-gray-500" />
      </div>

      {/* Search & Filters */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by EWB number, invoice, shipment..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${showFilters ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] text-[var(--ink-0)]' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
        >
          <Filter size={16} />
          Filters
        </button>
      </div>

      {showFilters && (
        <div className="mb-4 flex flex-wrap gap-3 rounded-lg border border-gray-200 bg-white p-4">
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
          >
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
          />
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
          />
          {(filterStatus || dateFrom || dateTo) && (
            <button
              onClick={() => { setFilterStatus(''); setDateFrom(''); setDateTo(''); }}
              className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
            <span className="ml-2 text-sm text-gray-500">Loading e-Way Bill logs...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-red-500">
            <p className="text-sm">{error}</p>
            <button onClick={fetchLogs} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <Truck size={40} className="mb-2" />
            <p className="text-sm">No e-Way Bill records found</p>
            <p className="text-xs">e-Way Bills will appear here once generated for shipments or invoices.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('ewayBillNo')}>
                    <span className="flex items-center gap-1">EWB No. <SortIcon field="ewayBillNo" /></span>
                  </th>
                  <th className="px-4 py-3">Invoice / Shipment</th>
                  <th className="px-4 py-3">From &rarr; To</th>
                  <th className="cursor-pointer px-4 py-3 text-right" onClick={() => handleSort('totalValue')}>
                    <span className="flex items-center justify-end gap-1">Value <SortIcon field="totalValue" /></span>
                  </th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('validUntil')}>
                    <span className="flex items-center gap-1">Valid Until <SortIcon field="validUntil" /></span>
                  </th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-center w-36">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-[var(--ink-0)] font-mono text-xs">{log.ewayBillNo || 'Pending'}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {log.invoiceNumber && <span className="block">{log.invoiceNumber}</span>}
                      {log.shipmentRef && <span className="block text-xs text-gray-400">{log.shipmentRef}</span>}
                      {!log.invoiceNumber && !log.shipmentRef && '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {log.fromPlace} &rarr; {log.toPlace}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">{formatINR(log.totalValue)}</td>
                    <td className={`px-4 py-3 text-xs ${getExpiryColor(log.validUntil)}`}>
                      {formatValidUntil(log.validUntil)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[log.status] || STATUS_COLORS.GENERATED}`}>
                        {log.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {/* View Details */}
                        <button
                          onClick={() => setDetailLog(log)}
                          className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:bg-[var(--saffron-50)] hover:text-[var(--ink-0)] transition-colors"
                          title="View Details"
                        >
                          <Eye size={15} />
                        </button>
                        {/* Generate */}
                        {!log.ewayBillNo && (
                          <button
                            onClick={() => handleGenerate(log)}
                            disabled={generating === log.id}
                            className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:bg-green-50 hover:text-green-600 transition-colors disabled:opacity-50"
                            title="Generate e-Way Bill"
                          >
                            {generating === log.id ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
                          </button>
                        )}
                        {/* Update Vehicle */}
                        {log.status === 'ACTIVE' && (
                          <button
                            onClick={() => { setVehicleTarget(log); setVehicleNumber(log.vehicleNumber || ''); setVehicleType(log.vehicleType || 'Regular'); }}
                            className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                            title="Update Vehicle"
                          >
                            <Edit3 size={15} />
                          </button>
                        )}
                        {/* Extend Validity */}
                        {log.status === 'ACTIVE' && (
                          <button
                            onClick={() => { setExtendTarget(log); setExtendReason(''); }}
                            className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:bg-yellow-50 hover:text-yellow-600 transition-colors"
                            title="Extend Validity"
                          >
                            <TimerReset size={15} />
                          </button>
                        )}
                        {/* Cancel */}
                        {canCancelEWB(log) && (
                          <button
                            onClick={() => { setCancelTarget(log); setCancelReason(''); }}
                            className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                            title="Cancel e-Way Bill (within 24h)"
                          >
                            <RotateCcw size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
            <div className="text-sm text-gray-500">
              Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1 text-sm rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
              <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="px-3 py-1 text-sm rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* ==================== Detail Modal ==================== */}
      {detailLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDetailLog(null)}>
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">e-Way Bill Details</h3>
              <button onClick={() => setDetailLog(null)} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100"><X size={18} /></button>
            </div>
            <div className="space-y-3 text-sm">
              <DetailRow label="EWB No." value={detailLog.ewayBillNo || 'Pending'} mono />
              <DetailRow label="Invoice #" value={detailLog.invoiceNumber || '-'} />
              <DetailRow label="Shipment" value={detailLog.shipmentRef || '-'} />
              <DetailRow label="From" value={detailLog.fromPlace} />
              <DetailRow label="To" value={detailLog.toPlace} />
              <DetailRow label="Value" value={formatINR(detailLog.totalValue)} />
              <DetailRow label="Vehicle" value={detailLog.vehicleNumber || '-'} />
              <DetailRow label="Vehicle Type" value={detailLog.vehicleType || '-'} />
              <DetailRow label="Transporter" value={detailLog.transporterName || '-'} />
              <DetailRow label="Status" value={
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[detailLog.status]}`}>{detailLog.status}</span>
              } />
              <DetailRow label="Valid Until" value={
                <span className={getExpiryColor(detailLog.validUntil)}>{formatValidUntil(detailLog.validUntil)}</span>
              } />
              {detailLog.generatedAt && <DetailRow label="Generated At" value={formatDate(detailLog.generatedAt)} />}
              {detailLog.cancelledAt && <DetailRow label="Cancelled At" value={formatDate(detailLog.cancelledAt)} />}
              {detailLog.errorMessage && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                  <p className="font-medium">Error:</p>
                  <p>{detailLog.errorMessage}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ==================== Cancel Modal ==================== */}
      {cancelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setCancelTarget(null)}>
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Cancel e-Way Bill</h3>
              <button onClick={() => setCancelTarget(null)} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100"><X size={18} /></button>
            </div>
            <p className="mb-3 text-sm text-gray-600">
              Cancel e-Way Bill <strong>{cancelTarget.ewayBillNo}</strong>? This action cannot be undone.
            </p>
            <label className="mb-1 block text-sm font-medium text-gray-700">Reason for cancellation *</label>
            <textarea
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
              placeholder="Enter reason for cancelling this e-Way Bill..."
            />
            <div className="mt-4 flex justify-end gap-3">
              <button onClick={() => setCancelTarget(null)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                Close
              </button>
              <button
                onClick={handleCancel}
                disabled={!cancelReason.trim() || !!cancelling}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {cancelling ? <Loader2 size={14} className="animate-spin" /> : null}
                Cancel EWB
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== Update Vehicle Modal ==================== */}
      {vehicleTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setVehicleTarget(null)}>
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Update Vehicle Details</h3>
              <button onClick={() => setVehicleTarget(null)} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100"><X size={18} /></button>
            </div>
            <p className="mb-3 text-sm text-gray-500">
              Update vehicle info for EWB: <strong>{vehicleTarget.ewayBillNo}</strong>
            </p>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Vehicle Number *</label>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm uppercase focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  value={vehicleNumber}
                  onChange={e => setVehicleNumber(e.target.value)}
                  placeholder="HR 26 AB 1234"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Vehicle Type</label>
                <select
                  value={vehicleType}
                  onChange={e => setVehicleType(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                >
                  <option value="Regular">Regular</option>
                  <option value="Over Dimensional Cargo">Over Dimensional Cargo (ODC)</option>
                </select>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-3">
              <button onClick={() => setVehicleTarget(null)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                Close
              </button>
              <button
                onClick={handleUpdateVehicle}
                disabled={!vehicleNumber.trim() || vehicleSaving}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
              >
                {vehicleSaving ? <Loader2 size={14} className="animate-spin" /> : null}
                Update Vehicle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== Extend Validity Modal ==================== */}
      {extendTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setExtendTarget(null)}>
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Extend Validity</h3>
              <button onClick={() => setExtendTarget(null)} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100"><X size={18} /></button>
            </div>
            <p className="mb-3 text-sm text-gray-500">
              Extend validity of EWB: <strong>{extendTarget.ewayBillNo}</strong>
              {extendTarget.validUntil && (
                <span className="block mt-1">Current validity: <strong className={getExpiryColor(extendTarget.validUntil)}>{formatValidUntil(extendTarget.validUntil)}</strong></span>
              )}
            </p>
            <label className="mb-1 block text-sm font-medium text-gray-700">Reason for extension *</label>
            <textarea
              value={extendReason}
              onChange={e => setExtendReason(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
              placeholder="e.g., Vehicle breakdown, natural disaster, trans-shipment..."
            />
            <div className="mt-4 flex justify-end gap-3">
              <button onClick={() => setExtendTarget(null)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                Close
              </button>
              <button
                onClick={handleExtendValidity}
                disabled={!extendReason.trim() || extendSaving}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
              >
                {extendSaving ? <Loader2 size={14} className="animate-spin" /> : null}
                Extend Validity
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== Sub-components ====================
function StatCard({ label, value, icon, color }: { label: string; value: number; icon: React.ReactNode; color: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-center gap-2 text-gray-400">{icon}<p className="text-xs text-gray-500">{label}</p></div>
      <p className={`mt-1 text-lg font-bold ${color}`}>{value}</p>
    </div>
  );
}

function DetailRow({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className={`text-right text-gray-900 ${mono ? 'font-mono text-xs break-all' : ''}`}>{value}</span>
    </div>
  );
}

export default EWayBillDashboard;
